import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStatus } from '../Auth/AuthStatusContext';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Button,
    CircularProgress,
    Alert,
    Grid,
    TextField,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Select,
    MenuItem,
    FormControl,
    InputLabel,

    Paper,
    SelectChangeEvent
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import OrganizationMemberService from '../../services/OrganizationMemberService';
import { OrganizationMemberDTO, UpdateMemberRequest, Role } from '../../types/OrganizationMember';
import RolePill from '../Common/RolePill';
import { validateEmail } from '../../utils/validation';

interface OrganizationUsersPanelProps {
    expanded: boolean;
    onToggle: (event: React.SyntheticEvent, expanded: boolean) => void;
    organizationId: string;
}

const OrganizationUsersPanel: React.FC<OrganizationUsersPanelProps> = ({ expanded, onToggle, organizationId }) => {
    const { profile } = useAuthStatus();
    const [members, setMembers] = useState<OrganizationMemberDTO[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editingUserId, setEditingUserId] = useState<string | null>(null);
    const [userToDelete, setUserToDelete] = useState<OrganizationMemberDTO | null>(null);
    const [editedUserData, setEditedUserData] = useState<Partial<UpdateMemberRequest>>({});
    const [isAddMemberDialogOpen, setIsAddMemberDialogOpen] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [newMemberData, setNewMemberData] = useState({ email: '', firstName: '', lastName: '', role: 'MEMBER' as Role });

    const fetchMembers = useCallback(async () => {
        if (organizationId) {
            setIsLoading(true);
            setError(null);
            try {
                const data = await OrganizationMemberService.listMembers(organizationId);
                setMembers(data || []);
            } catch (err) {
                setError('Failed to load organization members.');
                console.error(err);
            } finally {
                setIsLoading(false);
            }
        }
    }, [organizationId]);

    useEffect(() => {
        if (expanded) {
            fetchMembers();
        }
    }, [expanded, fetchMembers]);

    const handleEditClick = (member: OrganizationMemberDTO) => {
        setEditingUserId(member.id);
        setEditedUserData({
            first_name: member.first_name,
            last_name: member.last_name,
            organization_member_role: member.organization_member_role as Role,
        });
    };

    const handleCancelClick = () => {
        setEditingUserId(null);
        setEditedUserData({});
    };

    const handleSaveClick = async (userId: string) => {
        if (!editedUserData) return;
        try {
            await OrganizationMemberService.updateMember(organizationId, userId, editedUserData as UpdateMemberRequest);
            setMembers(members.map((m: OrganizationMemberDTO) => m.id === userId ? ({ ...m, ...editedUserData, organization_member_role: editedUserData.organization_member_role || m.organization_member_role } as OrganizationMemberDTO) : m));
            handleCancelClick();
        } catch (err) {
            setError('Failed to update user.');
            console.error(err);
        }
    };

    const handleDeleteConfirm = async () => {
        if (userToDelete) {
            try {
                await OrganizationMemberService.removeMember(organizationId, userToDelete.id);
                fetchMembers();
            } catch (err) {
                setError('Failed to remove user.');
                console.error(err);
            } finally {
                setUserToDelete(null);
            }
        }
    };

    const handleCloseAddMemberDialog = () => {
        setIsAddMemberDialogOpen(false);
        setNewMemberData({ email: '', firstName: '', lastName: '', role: 'MEMBER' as Role });
        setEmailError(null);
    };

    const handleInvite = async () => {
        if (!validateEmail(newMemberData.email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }

        setIsInviting(true);
        setError(null);
        setEmailError(null);

        try {
            await OrganizationMemberService.addMember(organizationId, {
                user_email: newMemberData.email,
                first_name: newMemberData.firstName,
                last_name: newMemberData.lastName,
                organization_member_role: newMemberData.role,
            });

            handleCloseAddMemberDialog();
            fetchMembers();
        } catch (err) {
            setError('Failed to invite member. Please check the email and try again.');
            console.error(err);
        } finally {
            setIsInviting(false);
        }
    };

    return (
        <Accordion expanded={expanded} onChange={onToggle}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <Typography variant="h6">Members</Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsAddMemberDialogOpen(true);
                        }}
                        sx={{ mr: 2 }}
                        data-testid="add-member-button"
                    >
                        Add Member
                    </Button>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress />
                    </Box>
                ) : error ? (
                    <Alert severity="error">{error}</Alert>
                ) : (
                    <Box>
                        {members.map((member: OrganizationMemberDTO) => (
                            <Paper key={member.id} sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                {editingUserId === member.id ? (
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="First Name"
                                                defaultValue={member.first_name}
                                                onChange={(e) => setEditedUserData({ ...editedUserData, first_name: e.target.value })}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                label="Last Name"
                                                defaultValue={member.last_name}
                                                onChange={(e) => setEditedUserData({ ...editedUserData, last_name: e.target.value })}
                                                fullWidth
                                                variant="outlined"
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <FormControl fullWidth size="small">
                                                <InputLabel>Role</InputLabel>
                                                <Select
                                                    label="Role"
                                                    value={editedUserData.organization_member_role || member.organization_member_role || ''}
                                                    onChange={(e) => setEditedUserData({ ...editedUserData, organization_member_role: e.target.value as Role })}
                                                >
                                                    <MenuItem value="ADMIN">Admin</MenuItem>
                                                    <MenuItem value="MEMBER">Member</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton onClick={() => handleSaveClick(member.id)}><SaveIcon /></IconButton>
                                            <IconButton onClick={handleCancelClick}><CancelIcon /></IconButton>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', width: '30%' }}>
                                                {`${member.first_name || ''} ${member.last_name || ''}`.trim()}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">{member.email}</Typography>
                                        </Grid>
                                        <Grid item xs={6} sm={2}>
                                            <RolePill role={member.organization_member_role} size="small" />
                                        </Grid>
                                        <Grid item xs={6} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton onClick={() => handleEditClick(member)} size="small" disabled={member.id === profile?.id}><EditIcon /></IconButton>
                                            <IconButton onClick={() => setUserToDelete(member)} disabled={member.id === profile?.id}><DeleteIcon /></IconButton>
                                        </Grid>
                                    </Grid>
                                )}
                            </Paper>
                        ))}
                    </Box>
                )}
                <Dialog open={isAddMemberDialogOpen} onClose={handleCloseAddMemberDialog}>
                    <DialogTitle>Invite New Member</DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        <DialogContentText>
                            To invite a new member to this organization, please enter their details and assign a role.
                        </DialogContentText>
                        <TextField
                            autoFocus
                            margin="dense"
                            id="firstName"
                            label="First Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newMemberData.firstName}
                            onChange={(e) => setNewMemberData({ ...newMemberData, firstName: e.target.value })}
                            sx={{ mt: 2 }}
                        />
                        <TextField
                            margin="dense"
                            id="lastName"
                            label="Last Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={newMemberData.lastName}
                            onChange={(e) => setNewMemberData({ ...newMemberData, lastName: e.target.value })}
                        />
                        <TextField
                            margin="dense"
                            id="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                            required
                            variant="outlined"
                            value={newMemberData.email}
                            error={!!emailError}
                            helperText={emailError}
                            onChange={(e) => {
                                setNewMemberData({ ...newMemberData, email: e.target.value });
                                if (emailError) {
                                    setEmailError(null);
                                }
                            }}
                        />
                        <FormControl fullWidth margin="dense">
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={newMemberData.role}
                                label="Role"
                                onChange={(e: SelectChangeEvent<Role>) => setNewMemberData({ ...newMemberData, role: e.target.value as Role })}
                            >
                                <MenuItem value="MEMBER">Member</MenuItem>
                                <MenuItem value="ADMIN">Admin</MenuItem>
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ p: 3, pt: 0 }}>
                        <Button onClick={handleCloseAddMemberDialog} variant="outlined">Cancel</Button>
                        <Button onClick={handleInvite} disabled={isInviting || !validateEmail(newMemberData.email)} variant="contained" color="primary">
                            {isInviting ? <CircularProgress size={24} color="inherit" /> : 'Send Invitation'}
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={!!userToDelete}
                    onClose={() => setUserToDelete(null)}
                >
                    <DialogTitle>{"Confirm Deletion"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to remove this user from the organization? This action cannot be undone.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setUserToDelete(null)}>Cancel</Button>
                        <Button onClick={handleDeleteConfirm} color="error" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </AccordionDetails>
        </Accordion>
    );
};

export default OrganizationUsersPanel;
